import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import ServiceGlyph from '../ServiceGlyph';
import { tie } from '../page/typography';

// «Тим, хто…» після «Кому:» — з малої. Абревіатуру на початку («КВЕДи під
// реальні послуги») не чіпаємо: друга літера велика — отже, це не слово.
const lowerFirst = (value) =>
  value.charAt(1) === value.charAt(1).toLowerCase() ? value.charAt(0).toLowerCase() + value.slice(1) : value;

/**
 * Картка напрямку на сторінці всіх послуг.
 *
 * Та сама родина, що й на головній: фото напрямку, малюнок на межі фото й
 * тексту, назва. Але тут людина вже не знайомиться з компанією, а обирає, куди
 * йти, тож картка каже інше:
 * - «Кому» — перший пункт із «Кому це потрібно» на сторінці послуги. Назва
 *   «Юридичний аутсорсинг» нічого не скаже лікарю, а «клінікам без штатного
 *   юриста» — одразу;
 * - «Що входить» — кроки роботи зі сторінки послуги (або пункти «Що входить»,
 *   де кроків немає) одним рядком через кому. Раніше тут стояли перші три
 *   пункти «Що входить», і в аутсорсингу це були «Тематика супроводу» й
 *   «Перевага» — підписи, а не зміст. Плашками ті самі кроки розповзалися
 *   на п'ять рядків у ліцензуванні й на три у сусідній картці, і ряд
 *   карток ставав рваним;
 * - підзаголовок із головної («Правова підтримка Вашого бізнесу») не
 *   показуємо: він загальний, а два загальні речення поспіль картку лише
 *   подовжували.
 *
 * Фото — низькою смугою 3:1, а не 16:10: на широкому екрані шість карток з
 * великими фото займали два з половиною екрани, і сам вибір губився між
 * знімками. Номерів 01–06 теж немає — порядку в напрямках немає.
 *
 * Посилання — на назві, а на всю картку його розтягує ::after. Так уся картка
 * натискається, а диктор читає назву посилання як назву напрямку, а не весь
 * текст картки підряд.
 */
const DirectionCard = ({ group, meta, index }) => {
  const { slug, banner } = meta;
  const small = banner.replace(/\.jpg$/, '-sm.jpg');
  const steps = (group.process || group.items || []).map((step) => step.label);
  const who = group.audience && group.audience[0];

  // Від md картка — підсітка з п'яти рядків батьківської сітки (фото, назва,
  // «Кому», «Що входить», стрілка). Рядки спільні для всього ряду карток,
  // тож риска над «Що входить» стоїть на одній висоті в усіх трьох, хоч би
  // скільки рядків займала назва чи «Кому» в сусідки.
  return (
    <article className="group relative h-full flex flex-col md:grid md:grid-rows-subgrid md:row-span-5 md:gap-y-0 rounded-2xl border border-slate-200 bg-white transition-[box-shadow,border-color] duration-300 hover:border-teal-300 hover:shadow-md">
      {/* На телефоні фото немає: шість смуг по сто двадцять пікселів — це
          ще екран гортання, а напрямок і так видно з малюнка й назви. */}
      <div className="hidden sm:block relative aspect-[3/1] overflow-hidden rounded-t-[calc(1rem-1px)] bg-slate-100">
        <img
          src={process.env.PUBLIC_URL + small}
          srcSet={`${process.env.PUBLIC_URL + small} 800w, ${process.env.PUBLIC_URL + banner} 1600w`}
          sizes="(min-width: 1280px) 400px, (min-width: 768px) 50vw, 100vw"
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-[center_35%] transition-transform duration-500 motion-reduce:transition-none group-hover:scale-105"
        />
      </div>

      <div className="flex items-center gap-4 px-5 pt-5 sm:block sm:px-6 sm:pt-0 lg:px-7">
        <span className="relative flex-shrink-0 w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-sm text-teal-700 flex items-center justify-center sm:-mt-6 sm:mb-4" aria-hidden="true">
          <ServiceGlyph index={index} className="w-7 h-7" />
        </span>

        <h3 className="text-lg sm:text-xl font-bold font-display text-slate-900 leading-snug text-balance">
          <Link
            to={`/poslugy/${slug}`}
            className="transition-colors duration-200 group-hover:text-teal-800 focus:outline-none after:absolute after:inset-0 after:rounded-2xl focus-visible:after:ring-2 focus-visible:after:ring-teal-700 focus-visible:after:ring-offset-2"
          >
            {group.title}
          </Link>
        </h3>
      </div>

      <p className="px-5 sm:px-6 lg:px-7 mt-3 text-[0.95rem] leading-relaxed text-slate-600">
        {who && (
          <>
            <span className="font-semibold text-slate-800">Кому:</span> {tie(lowerFirst(who))}
          </>
        )}
      </p>

      <p className="mx-5 sm:mx-6 lg:mx-7 mt-4 pt-4 border-t border-slate-100 text-sm leading-relaxed text-slate-600">
        {steps.length > 0 && (
          <>
            <span className="font-semibold text-slate-800">Що входить:</span> {tie(`${steps.map(lowerFirst).join(', ')}.`)}
          </>
        )}
      </p>

      {/* Лише підказка для ока: посилання вже є на назві, і друге з тим
          самим змістом диктор прочитав би двічі. */}
      <span className="mt-auto px-5 pt-5 pb-5 sm:px-6 sm:pt-6 sm:pb-6 lg:px-7 lg:pb-7 inline-flex items-center gap-2 text-sm font-semibold text-teal-700" aria-hidden="true">
        Детальніше про послугу
        <Icon name="fa-arrow-right" className="transition-transform duration-300 motion-reduce:transition-none group-hover:translate-x-1" />
      </span>
    </article>
  );
};

export default DirectionCard;
